import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import * as InvestorPortalService from '@/services/investorPortalService';

export function useInvestorPortal() {
  const { user } = useAuth();
  const [holdings, setHoldings] = useState<InvestorPortalService.InvestorHolding[]>([]);
  const [certificates, setCertificates] = useState<InvestorPortalService.InvestorCertificate[]>([]);
  const [valuations, setValuations] = useState<InvestorPortalService.InvestorValuation[]>([]);
  const [reports, setReports] = useState<Map<string, InvestorPortalService.InvestorReport[]>>(
    new Map()
  );
  const [messages, setMessages] = useState<Map<string, InvestorPortalService.InvestorMessage[]>>(
    new Map()
  );
  const [portfolioSummary, setPortfolioSummary] =
    useState<InvestorPortalService.PortfolioSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Prevent concurrent data fetches and track user ID without causing re-renders
  const isLoadingRef = useRef(false);
  const userIdRef = useRef(user?.id);
  const mountCountRef = useRef(0);
  const lastLoadTimeRef = useRef(0);

  // Cooldown period: minimum 5 seconds between data loads
  const COOLDOWN_MS = 5000;

  // Update userIdRef when user changes
  useEffect(() => {
    userIdRef.current = user?.id;
    mountCountRef.current += 1;
    console.log('[InvestorPortal] Mount/update #', mountCountRef.current, 'user:', user?.id);
  }, [user?.id]);

  // Stable loadData reference - no dependencies
  const loadData = useCallback(async () => {
    const currentUserId = userIdRef.current;
    if (!currentUserId || isLoadingRef.current) return;

    // Check cooldown period
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    if (lastLoadTimeRef.current > 0 && timeSinceLastLoad < COOLDOWN_MS) {
      console.log(
        '[InvestorPortal] Skipping load (cooldown):',
        timeSinceLastLoad,
        'ms since last load'
      );
      return;
    }

    console.log('[InvestorPortal] loadData called for user:', currentUserId);
    isLoadingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const data = await InvestorPortalService.getInvestorPortalData(currentUserId);

      setHoldings(data.holdings);
      setCertificates(data.certificates);
      setValuations(data.valuations);
      setReports(data.reports);
      setMessages(data.messages);
      setPortfolioSummary(data.portfolioSummary);

      // Update last load timestamp on success
      lastLoadTimeRef.current = Date.now();
    } catch (err) {
      setError(err as Error);
      console.error('Error loading investor portal:', err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []); // Empty deps - stable reference

  // Load data when user changes - only depend on user
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user?.id, loadData]);

  return {
    holdings,
    certificates,
    valuations,
    reports,
    messages,
    portfolioSummary,
    loading,
    error,
    refresh: loadData,
  };
}
